import React, { useMemo, useState } from 'react';
import { Field, type FieldProps } from 'formik';
import { useDebounce } from 'react-use';
import { useAsync } from 'react-async';
import KoobInputSelect from '../KoobInputSelect';
import KoobInputLabel from '../KoobInputLabel';

type KoobInputSelectFieldProps = {
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Show the error before the field is touched when false */
  touchRequired?: boolean;
  /** Multi-select: formik holds an array of items */
  multiple?: boolean;
  /** multiple only: called with the newly added item */
  onSelectOption?: (item: any) => void;
  /** multiple only: called with the removed item */
  onUnselectOption?: (item: any) => void;
  /** Sort/decorate fetched items before they become options */
  transformItems?: (items: any[]) => any[];
  /** Local items — no fetching, internal search filtering */
  items?: any[];
  /**
   * Remote search: react-async promiseFn. Called with both historical
   * conventions so existing api functions work unchanged:
   * { search, locale, ...variables, filters: { search, perPage, page, ...variables } }
   */
  query?: (props: any) => Promise<any>;
  /**
   * Run `query` on the first open instead of at mount: for forms that render
   * many of these fields at once (one per day, per option), where loading
   * every list up front sends a burst of identical requests.
   */
  lazy?: boolean;
  variables?: Record<string, any>;
  /** Option label from an item */
  renderOption?: (item: any) => React.ReactNode;
  /**
   * Decorates the option row in the dropdown without affecting the label
   * used for search matching and data-testid (e.g. trailing icons).
   */
  renderOptionContent?: (
    item: any,
    state: { selected: boolean; focused: boolean },
  ) => React.ReactNode;
  /** Trigger label for the selected item (defaults to renderOption) */
  renderValue?: (item: any) => React.ReactNode;
  /** Called with the picked item after formik is updated */
  onAfterChange?: (item: any) => void;
  /** Raw (undebounced-source, debounced-emit) search term as the user types */
  onSearchChange?: (term: string) => void;
  'data-testid'?: string;
};

const keyOf = (item: any) =>
  typeof item === 'object' ? (item?.id ?? item?.value) : item || undefined;

// formik errors on array values can be arrays — show the first real message
const formatError = (error: any) =>
  Array.isArray(error) ? error.filter(Boolean)?.[0] : error;

export const KoobInputSelectField = ({
  name,
  label,
  required,
  disabled,
  placeholder,
  touchRequired = true,
  multiple = false,
  onSelectOption,
  onUnselectOption,
  transformItems,
  items,
  query,
  lazy = false,
  variables,
  renderOption,
  renderOptionContent,
  renderValue,
  onAfterChange,
  onSearchChange,
  'data-testid': dataCy,
}: KoobInputSelectFieldProps) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [opened, setOpened] = useState(!lazy);

  useDebounce(
    () => {
      setDebouncedSearch(search);
      onSearchChange?.(search);
    },
    500,
    [search],
  );

  const filters = useMemo(
    () => ({
      search: debouncedSearch,
      // some multi-select endpoints (experiences) filter on searchName instead
      ...(multiple ? { searchName: debouncedSearch } : {}),
      perPage: variables?.perPage ?? 1000,
      page: variables?.page ?? 1,
      ...(variables ?? {}),
    }),
    [debouncedSearch, variables, multiple],
  );

  const { data: asyncData, isPending } = useAsync({
    promiseFn: items || !opened ? undefined : query,
    search: debouncedSearch,
    locale: variables?.locale,
    ...(variables ?? {}),
    filters,
    watchFn: (props: any, prevProps: any) =>
      JSON.stringify(props.filters) !== JSON.stringify(prevProps.filters),
  });

  const fetchedItems = items || asyncData?.data?.data || asyncData?.data || [];
  const resolvedItems = transformItems
    ? transformItems(fetchedItems)
    : fetchedItems;

  const toLabel = renderOption ?? ((item: any) => item?.label ?? keyOf(item));

  return (
    <Field name={name}>
      {({ field, meta, form }: FieldProps) => {
        const error = formatError(meta.error);
        const hasError = touchRequired
          ? Boolean(error && meta.touched)
          : Boolean(error);

        const options = resolvedItems.map((item: any) => ({
          value: keyOf(item),
          label: toLabel(item),
          item,
        }));
        // The selected value(s) may not be part of the current search
        // results; keep them in the list so the trigger can display them.
        const selected: any[] = multiple
          ? (field.value ?? [])
          : field.value != null && field.value !== ''
            ? [field.value]
            : [];
        selected.forEach((selectedItem) => {
          const key = keyOf(selectedItem);
          if (
            key != null &&
            !options.some((option: any) => option.value === key)
          ) {
            options.unshift({
              value: key,
              label: (renderValue ?? toLabel)(selectedItem),
              item: selectedItem,
            });
          }
        });
        const selectedKeys = selected.map(keyOf);

        const handleSingleChange = (v: any) => {
          const item =
            options.find((option: any) => option.value === v)?.item ?? null;
          form.setFieldValue(name, item);
          onAfterChange?.(item);
        };

        const handleMultipleChange = (values: any[]) => {
          const next = (values ?? [])
            .map((v) => options.find((option: any) => option.value === v)?.item)
            .filter(Boolean);
          form.setFieldValue(name, next);
          const added = next.find(
            (item: any) => !selectedKeys.includes(keyOf(item)),
          );
          if (added) {
            onSelectOption?.(added);
          }
          const removed = selected.find(
            (item: any) => !next.some((n: any) => keyOf(n) === keyOf(item)),
          );
          if (removed) {
            onUnselectOption?.(removed);
          }
          onAfterChange?.(next);
        };

        const select = (
          <KoobInputSelect
            searchable
            multiple={multiple}
            options={options}
            loading={!items && isPending && options.length === 0}
            value={multiple ? selectedKeys : (selectedKeys[0] ?? null)}
            onSearchChange={items ? undefined : setSearch}
            onOpen={opened ? undefined : () => setOpened(true)}
            onValueChange={multiple ? handleMultipleChange : handleSingleChange}
            renderOption={
              renderOptionContent
                ? (option: any, state: any) =>
                    renderOptionContent(option.item, state)
                : undefined
            }
            placeholder={placeholder}
            error={hasError}
            disabled={disabled}
            data-testid={dataCy ?? `${name?.toLowerCase()}-select`}
          />
        );

        return (
          <div className="flex w-full flex-1 flex-col gap-1">
            {label != null ? (
              <KoobInputLabel
                label={label}
                required={required}
                disabled={disabled}
              >
                {select}
              </KoobInputLabel>
            ) : (
              select
            )}
            {hasError && (
              <p className="m-0 text-sm text-red-500">{error as any}</p>
            )}
          </div>
        );
      }}
    </Field>
  );
};

export default KoobInputSelectField;
